import React from 'react';
import { PropTypes } from 'prop-types';
import { Col, Grid, Row, Text } from 'rsuite';
import { PresentationCol, PresentationRow } from './PresentationLayout';
import FlowProgressIndicator from '../user-feedback/FlowProgressIndicator';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentStep,
  selectFlowSteps,
  selectNextStepPath,
  selectOriginPath,
  selectPrevStepPath,
} from '../../store/slices/flow/flowSelectors';
import {
  resetStepState,
  setCurrentStep,
  setStepCompletedStatus,
} from '../../store/slices/flow/flowSlice';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import PageButtons from './PageButtons';

/** THIS COMPONENT IS BEING DEPRECATED - USE FLOWLAYOUT INSTEAD */
const FlowPageLayout = ({
  children,
  title,
  subtitle = '',
  onNext = async () => {
    return true;
  },
  onBack = async () => {
    return true;
  },
  nextButtonText = 'Continue',
  backButtonText = 'Back',
  hideNextButton = false,
  hideBackButton = false,
  disableNextButton = false,
  omitNavigationForward = false,
}) => {
  const dispatch = useDispatch();
  const placeNavigate = usePlaceNavigate();
  const selectedSteps = useSelector(selectFlowSteps);
  const selectedCurrentStep = useSelector(selectCurrentStep);
  const selectedNextStepPath = useSelector(selectNextStepPath);
  const selectedPrevStepPath = useSelector(selectPrevStepPath);
  const selectedOriginPath = useSelector(selectOriginPath);

  const goToPrevStep = (e) => {
    e.target.blur();
    if (!onBack()) return;
    let prevPath = '/' + selectedPrevStepPath;
    if (selectedCurrentStep <= 1) {
      prevPath = selectedOriginPath;
      dispatch(resetStepState());
    } else {
      dispatch(
        setStepCompletedStatus({ stepNum: selectedCurrentStep, status: false })
      );
      dispatch(setCurrentStep(selectedCurrentStep - 1));
    }
    placeNavigate(prevPath);
  };

  const goToNextStep = async (e) => {
    e.target.blur();
    if (disableNextButton) return;
    if (!(await onNext())) return;
    if (!omitNavigationForward) {
      dispatch(
        setStepCompletedStatus({ stepNum: selectedCurrentStep, status: true })
      );
      dispatch(setCurrentStep(selectedCurrentStep + 1));
      placeNavigate('/' + selectedNextStepPath);
    }
  };

  return (
    <main
      id="main-content"
      tabIndex={-1}
      aria-label={title || undefined}
    >
      <Grid
        className="mt-24 sm-mt-0 pv-24 sm-pb-0"
        fluid
      >
        {/* Top row: a conditionally-rendered progress indicator on small
          viewports only. This is pure visual layout, not tabular data, so
          the Presentation* wrappers neutralise the implicit grid roles.
          The Row/Col below is the real 2-column page grid (content + step
          sidebar on lg), left as raw rsuite to keep grid semantics. */}
        <PresentationRow>
          <PresentationCol
            className="flow-page-horizontal-progress-indicator"
            xs={24}
            lgHidden
            xlHidden
            xxlHidden
          >
            {selectedSteps != null && (
              <FlowProgressIndicator
                indicatorState={selectedSteps}
                direction="horizontal"
                currentStateIndex={selectedCurrentStep}
              />
            )}
          </PresentationCol>
        </PresentationRow>
        <Row>
          <Col
            className="flow-page-layout-container"
            xs={24}
            lg={16}
          >
            <div className="flow-page-layout-panel-left">
              <Text
                as="h1"
                className="header-text-h3 mb-24"
                style={{ marginLeft: '-3px' }}
              >
                {title}
              </Text>
              {subtitle?.length > 0 && (
                <Text className="mb-24">{subtitle}</Text>
              )}
              <div className="content-section">{children}</div>
            </div>
            <div className=" flow-page-layout-panel-left">
              <PageButtons
                onNext={goToNextStep}
                onBack={goToPrevStep}
                showBack={!hideBackButton}
                showNext={!hideNextButton}
                disableNext={disableNextButton}
                next={nextButtonText}
                back={backButtonText}
              />
            </div>
          </Col>
          <Col
            lg={8}
            xsHidden
            smHidden
            mdHidden
            className="flow-page-layout-panel-right"
          >
            {!!selectedSteps && (
              <FlowProgressIndicator
                indicatorState={selectedSteps}
                currentStateIndex={selectedCurrentStep - 1}
              />
            )}
          </Col>
        </Row>
      </Grid>
    </main>
  );
};

FlowPageLayout.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  onNext: PropTypes.func,
  onBack: PropTypes.func,
  nextButtonText: PropTypes.string,
  backButtonText: PropTypes.string,
  hideNextButton: PropTypes.bool,
  hideBackButton: PropTypes.bool,
  disableNextButton: PropTypes.bool,
  omitNavigationForward: PropTypes.bool,
};

export default FlowPageLayout;
